/**
 * P5 AI Provider 凭证管理（嵌入 ControlPanel AI tab 顶部）
 *
 * - 列出 6 个 provider 卡片
 * - 编辑 API Key / Base URL / 默认模型 / 启用
 * - 测试连接
 */
import { useState } from 'react';
import { Modal, Input, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { Btn, Pill } from '../../components/ui';
import { fmtDateTime } from '../../lib/format';

const PROVIDER_META: Record<string, { icon: string; defaultModels: string[] }> = {
  claude:      { icon: '🤖', defaultModels: ['claude-opus-4-7', 'claude-sonnet-4-6', 'claude-haiku-4-5'] },
  openai:      { icon: '🌟', defaultModels: ['gpt-4o', 'gpt-4o-mini', 'gpt-3.5-turbo'] },
  deepseek:    { icon: '🧠', defaultModels: ['deepseek-chat', 'deepseek-reasoner'] },
  qwen:        { icon: '🐉', defaultModels: ['qwen-plus', 'qwen-turbo', 'qwen-max'] },
  aliyun_ocr:  { icon: '🔍', defaultModels: ['general'] },
  aliyun_face: { icon: '👤', defaultModels: ['face-recognition'] },
};

export function AiProvidersSection() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any>(null);
  const [showKey, setShowKey] = useState<Record<string, boolean>>({});

  const { data: providers } = useQuery<any[]>({
    queryKey: ['ai-providers'],
    queryFn: () => $.get('/ai-providers'),
  });

  const testMut = useMutation({
    mutationFn: (id: string) => $.post(`/ai-providers/${id}/test`, {}),
    onSuccess: (r: any, id: string) => {
      if (r.ok) {
        message.success(`${id} 连接成功 · ${r.latency_ms}ms`);
      } else {
        message.error(`${id} 连接失败：${r.error}`);
      }
      qc.invalidateQueries({ queryKey: ['ai-providers'] });
    },
    onError: (e: any) => message.error(e?.message ?? '测试失败'),
  });

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ fontSize: 16, fontWeight: 500, color: '#1F2329', marginBottom: 12 }}>
        🔑 Provider 凭证管理
      </div>
      <div style={{ fontSize: 12, color: '#646A73', marginBottom: 16 }}>
        每个 AI 服务商一个凭证（API Key 加密存储 / 解密只在调用时发生）。配 DB 后会覆盖 env 变量。
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
        {(providers ?? []).map((p: any) => (
          <ProviderCard
            key={p.providerId}
            p={p}
            onEdit={() => setEditing(p)}
            onTest={() => testMut.mutate(p.providerId)}
            testing={testMut.isPending && testMut.variables === p.providerId}
            showFullKey={!!showKey[p.providerId]}
            onToggleShow={() => setShowKey((s) => ({ ...s, [p.providerId]: !s[p.providerId] }))}
          />
        ))}
      </div>

      <EditProviderModal
        provider={editing}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ['ai-providers'] });
        }}
      />
    </div>
  );
}

function ProviderCard({ p, onEdit, onTest, testing, showFullKey, onToggleShow }: any) {
  const meta = PROVIDER_META[p.providerId] ?? { icon: '🔌', defaultModels: [] };
  const configured = p.configured;
  const lastTest = p.lastTestResult;

  return (
    <div style={{
      padding: 20, background: '#FFFFFF', borderRadius: 8, border: '1px solid #DEE0E3',
      lineHeight: 1.6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{meta.icon}</span>
        <span style={{ fontSize: 15, fontWeight: 500 }}>{p.displayName}</span>
        <span style={{ marginLeft: 'auto' }}>
          <Pill tone={p.enabled ? 'success' : 'default'}>{p.enabled ? '已启用' : '已停用'}</Pill>
        </span>
      </div>

      {!configured ? (
        <>
          <div style={{
            padding: 10, background: '#FFFBEA', borderRadius: 6, marginBottom: 10,
            fontSize: 12, color: '#B86700',
          }}>
            ⚠️ 未配置 API Key — {p.displayName} 功能将无法使用
          </div>
          <Btn kind="primary" onClick={onEdit}>+ 配置 API Key</Btn>
        </>
      ) : (
        <>
          <div style={{ fontSize: 11, color: '#646A73', marginBottom: 4 }}>API Key（来自 {p.source === 'db' ? 'DB' : 'env'}）</div>
          <div style={{
            padding: '8px 12px', background: '#F5F6F7', borderRadius: 6, marginBottom: 10,
            fontFamily: 'monospace', fontSize: 12, color: '#1F2329',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ flex: 1 }}>{p.apiKeyMasked || '—'}</span>
            <span style={{ fontSize: 10, color: '#8F959E' }}>🔒</span>
          </div>

          {p.baseUrl && (
            <div style={{ fontSize: 11, color: '#646A73', marginBottom: 10 }}>
              Base URL：<span style={{ fontFamily: 'monospace' }}>{p.baseUrl}</span>
            </div>
          )}
          <div style={{ fontSize: 11, color: '#646A73', marginBottom: 12 }}>
            默认模型：<strong style={{ color: '#1F2329' }}>{p.defaultModel ?? '—'}</strong>
          </div>

          {lastTest && (
            <div style={{
              padding: 8, marginBottom: 10, borderRadius: 6,
              background: lastTest.ok ? '#E8F8EB' : '#FFF0F0',
              color: lastTest.ok ? '#1F8231' : '#CF2C30',
              fontSize: 11,
            }}>
              {lastTest.ok
                ? `✓ 上次测试 ${lastTest.latency_ms}ms（${fmtDateTime(p.lastTestedAt)}）`
                : `✗ ${lastTest.error}`}
            </div>
          )}

          <div style={{ display: 'flex', gap: 6 }}>
            <Btn kind="default" onClick={onTest} disabled={testing}>
              {testing ? '测试中…' : '🔍 测试连接'}
            </Btn>
            <Btn kind="default" onClick={onEdit}>✏️ 编辑</Btn>
          </div>
        </>
      )}
    </div>
  );
}

function EditProviderModal({ provider, onClose, onDone }: any) {
  const isAliyun = provider?.providerId?.startsWith('aliyun_');
  const [form, setForm] = useState<any>(() => ({
    api_key: '',
    // 阿里云双字段（前端拼成 JSON 字符串发给后端，存进 api_key_encrypted）
    aliyun_ak: '',
    aliyun_sk: '',
    aliyun_region: 'cn-shanghai',
    base_url: provider?.baseUrl ?? '',
    default_model: provider?.defaultModel ?? '',
    enabled: provider?.enabled ?? true,
  }));

  // 切换 provider 时重置
  if (provider && (provider as any).__lastSeen !== form.__provider) {
    if (form.__provider !== provider.providerId) {
      // skip - keep stable
    }
  }

  const save = useMutation({
    mutationFn: () => {
      // 阿里云：把 AK + SK + region 合成 JSON 字符串塞进 api_key 字段
      const payload: any = { ...form };
      if (isAliyun) {
        if (form.aliyun_ak || form.aliyun_sk) {
          payload.api_key = JSON.stringify({
            access_key_id: form.aliyun_ak,
            access_key_secret: form.aliyun_sk,
            region: form.aliyun_region,
          });
        }
        delete payload.aliyun_ak;
        delete payload.aliyun_sk;
        delete payload.aliyun_region;
      }
      return $.put(`/ai-providers/${provider.providerId}`, payload);
    },
    onSuccess: () => { message.success('已保存'); onDone(); },
    onError: (e: any) => message.error(e?.message ?? '保存失败'),
  });

  if (!provider) return null;
  const meta = PROVIDER_META[provider.providerId] ?? { defaultModels: [] };

  return (
    <Modal
      title={`配置 ${provider.displayName}`}
      open={!!provider}
      onCancel={onClose}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? '保存中…' : '保存'}
          </Btn>
        </>
      }
      width={520}
    >
      {isAliyun ? (
        <>
          <Row label="AccessKey ID">
            <Input value={form.aliyun_ak}
                   onChange={(e) => setForm({ ...form, aliyun_ak: e.target.value })}
                   placeholder={provider.configured ? '留空 = 不修改' : 'LTAI...'} />
          </Row>
          <Row label="AccessKey Secret">
            <Input.Password value={form.aliyun_sk}
                            onChange={(e) => setForm({ ...form, aliyun_sk: e.target.value })}
                            placeholder={provider.configured ? '留空 = 不修改' : 'AK Secret'} />
          </Row>
          <Row label="Region">
            <select value={form.aliyun_region}
                    onChange={(e) => setForm({ ...form, aliyun_region: e.target.value })}
                    style={{ width: '100%', padding: 6, border: '1px solid #DEE0E3', borderRadius: 6 }}>
              <option value="cn-shanghai">cn-shanghai（华东 2 - 上海）</option>
              <option value="cn-hangzhou">cn-hangzhou（华东 1 - 杭州）</option>
              <option value="cn-beijing">cn-beijing（华北 2 - 北京）</option>
              <option value="cn-shenzhen">cn-shenzhen（华南 1 - 深圳）</option>
            </select>
            <div style={{ fontSize: 11, color: '#8F959E', marginTop: 4 }}>
              三个字段一起 AES-256 加密为 JSON 存 DB；前端永不显示完整明文。
            </div>
          </Row>
        </>
      ) : (
        <Row label="API Key">
          <Input.Password
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder={provider.configured ? '留空 = 不修改；输入新值覆盖' : '输入 API Key'}
          />
          <div style={{ fontSize: 11, color: '#8F959E', marginTop: 4 }}>
            AES-256 加密存储；前端永不显示完整明文，只 mask。
          </div>
        </Row>
      )}
      <Row label="Base URL（可选）">
        <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
               placeholder="留空走默认端点" />
      </Row>
      <Row label="默认模型">
        <select value={form.default_model}
                onChange={(e) => setForm({ ...form, default_model: e.target.value })}
                style={{ width: '100%', padding: 6, border: '1px solid #DEE0E3', borderRadius: 6 }}>
          <option value="">（未指定）</option>
          {meta.defaultModels.map((m: string) => <option key={m} value={m}>{m}</option>)}
        </select>
      </Row>
      <Row label="启用">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          <span style={{ fontSize: 13 }}>启用此 provider（停用后 AI 调用会 fallback 到其它）</span>
        </label>
      </Row>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: any }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, color: '#646A73', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
